import React, { useState, useEffect } from 'react';
import {
  Alert as AlertData,
  AlertStatus,
  AlertStatusLabels,
  AlertPatch,
  AlertMessageResponse,
  AlertStepsResponse
} from "@techgitter/contracts";
import {
  Drawer,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Divider,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { Close, ContentCopy, ExpandMore } from '@mui/icons-material';
import { patchAlert, getAlertMessage, getAlertSteps } from '../../services/alertsService';

interface AlertDetailDrawerProps {
  alert: AlertData | null;
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

export const AlertDetailDrawer: React.FC<AlertDetailDrawerProps> = ({
  alert,
  open,
  onClose,
  onUpdate
}) => {
  const [status, setStatus] = useState<AlertStatus>(AlertStatus.New);
  const [notes, setNotes] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [loadingSteps, setLoadingSteps] = useState(false);
  const [message, setMessage] = useState<AlertMessageResponse | null>(null);
  const [steps, setSteps] = useState<AlertStepsResponse | null>(null);

  useEffect(() => {
    if (alert) {
      setStatus(alert.status);
      setNotes(alert.notes || '');
      setMessage(null);
      setSteps(null);
    }
  }, [alert]);

  const handleSave = async () => {
    if (!alert) return;

    const patch: AlertPatch = {};
    let hasChanges = false;

    if (status !== alert.status) {
      patch.status = status;
      hasChanges = true;
    }
    if (notes !== (alert.notes || '')) {
      patch.notes = notes;
      hasChanges = true;
    }

    if (!hasChanges) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      await patchAlert(alert.id, patch);
      onUpdate();
      onClose();
    } catch (error) {
      window.alert(`Failed to update alert: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const loadMessage = async () => {
    if (!alert || !alert.messageId || message || loadingMessage) return;

    setLoadingMessage(true);
    try {
      const messageData = await getAlertMessage(alert.id);
      setMessage(messageData);
    } catch (error) {
      console.error('Failed to load message:', error);
      window.alert(`Failed to load message: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoadingMessage(false);
    }
  };

  const loadSteps = async () => {
    if (!alert || steps || loadingSteps) return;

    setLoadingSteps(true);
    try {
      const stepsData = await getAlertSteps(alert.id);
      setSteps(stepsData);
    } catch (error) {
      console.error('Failed to load steps:', error);
      window.alert(`Failed to load steps: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoadingSteps(false);
    }
  };

  const handleMessageAccordionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded && !message && !loadingMessage) {
      loadMessage();
    }
  };

  const handleStepsAccordionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded && !steps && !loadingSteps) {
      loadSteps();
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatRecordValue = (value: unknown) => {
    if (value === undefined) return '—';
    if (value === null) return 'null';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return JSON.stringify(value, null, 2);
  };

  const renderRecordTable = (record?: Record<string, unknown>) => {
    if (!record || Object.keys(record).length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          No data available
        </Typography>
      );
    }

    return (
      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: '#f5f5f5',
          maxHeight: 300,
          overflow: 'auto'
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: '35%' }}>Field</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(record).map(([key, value]) => (
              <TableRow key={key}>
                <TableCell sx={{ verticalAlign: 'top', fontWeight: 500 }}>{key}</TableCell>
                <TableCell
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace'
                  }}
                >
                  {formatRecordValue(value)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (!alert) return null;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 600, padding: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
          <Typography variant="h6">Alert Details</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        <Divider sx={{ marginBottom: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              onChange={(e) => setStatus(Number(e.target.value) as AlertStatus)}
              label="Status"
            >
              <MenuItem value={AlertStatus.New}>{AlertStatusLabels[AlertStatus.New]}</MenuItem>
              <MenuItem value={AlertStatus.Acknowledged}>{AlertStatusLabels[AlertStatus.Acknowledged]}</MenuItem>
              <MenuItem value={AlertStatus.Resolved}>{AlertStatusLabels[AlertStatus.Resolved]}</MenuItem>
              <MenuItem value={AlertStatus.NoActionRequired}>{AlertStatusLabels[AlertStatus.NoActionRequired]}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={4}
          />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              fullWidth
            >
              {saving ? <CircularProgress size={24} /> : 'Save'}
            </Button>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={saving}
              fullWidth
            >
              Cancel
            </Button>
          </Box>

          <Divider sx={{ marginY: 2 }} />

          <Typography variant="subtitle2" color="textSecondary">
            Full Detail
          </Typography>
          <Paper 
            sx={{ 
              padding: 2, 
              backgroundColor: '#f5f5f5', 
              maxHeight: 200, 
              overflow: 'auto',
              position: 'relative'
            }}
          >
            <IconButton
              size="small"
              sx={{ position: 'absolute', top: 8, right: 8 }}
              onClick={() => copyToClipboard(alert.detail || '')}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
            <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap' }}>
              {alert.detail || 'No detail available'}
            </pre>
          </Paper>

          {alert.messageId && (
            <Accordion onChange={handleMessageAccordionChange}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography>Related Message</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {loadingMessage ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : message ? (
                  renderRecordTable(message.record.raw)
                ) : null}
              </AccordionDetails>
            </Accordion>
          )}

          <Accordion onChange={handleStepsAccordionChange}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography>Related Failed Steps</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {loadingSteps ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : steps ? (
                <Box>
                  {steps.steps.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      No failed steps available
                    </Typography>
                  ) : (
                    steps.steps.map((step, index) => (
                      <Box key={index} sx={{ marginBottom: 2 }}>
                        <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
                          Step {index + 1}
                        </Typography>
                        {renderRecordTable(step.raw)}
                      </Box>
                    ))
                  )}
                </Box>
              ) : null}
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ marginY: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Message ID: {alert.messageId || 'N/A'}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Created: {new Date(alert.createdOn).toLocaleString()}
            </Typography>
            {alert.statusChangedBy && (
              <>
                <Typography variant="caption" color="textSecondary">
                  Changed By: {alert.statusChangedBy}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Changed On: {alert.statusChangedOn ? new Date(alert.statusChangedOn).toLocaleString() : 'N/A'}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};
